'use client'

import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  IconCards,
  IconLoader2,
  IconPlus,
  IconTrash
} from '@tabler/icons-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Textarea } from '@/components/ui/textarea'
import { useAuth } from '@/hooks/useAuth'
import {
  deleteStudyNoteFromDeck,
  getStudyNoteForEditor,
  saveStudyNoteToDeck
} from '@/lib/study'
import {
  generateStudyCards,
  getAvailableTemplateKeys,
  getDefaultTemplateKeys,
  getTemplateLabel,
  NOTE_TYPE_LABELS,
  validateStudyNoteInput
} from '@/lib/study-card-generation'
import type {
  StudyDeckItem,
  StudyNoteInput,
  StudyNoteType,
  StudyTemplateKey
} from '@/types/study'

type Props = {
  deckId: string
  item?: StudyDeckItem | null
  trigger?: React.ReactNode
  open?: boolean
  onOpenChange?: (open: boolean) => void
  onSaved?: () => void
}

type FormState = {
  noteType: StudyNoteType
  templateKeys: StudyTemplateKey[]
  dictEntryId: string
  simplified: string
  traditional: string
  pinyin: string
  zhuyin: string
  definitions: string
  sentence: string
  translation: string
  clozeAnswer: string
  customFront: string
  customBack: string
  sourceTitle: string
  tags: string
}

type FormStateKey = keyof FormState
type FormFieldKey = Exclude<FormStateKey, 'noteType' | 'templateKeys'>
type FormFieldConfig = {
  key: FormFieldKey
  label: string
  area?: boolean
  placeholder?: string
  wide?: boolean
}

const initialForm: FormState = {
  noteType: 'sentence',
  templateKeys: ['recognition'],
  dictEntryId: '',
  simplified: '',
  traditional: '',
  pinyin: '',
  zhuyin: '',
  definitions: '',
  sentence: '',
  translation: '',
  clozeAnswer: '',
  customFront: '',
  customBack: '',
  sourceTitle: '',
  tags: ''
}

function fieldString(fields: Record<string, unknown>, key: string) {
  const value = fields[key]
  return typeof value === 'string' ? value : ''
}

function definitionsString(
  fields: Record<string, unknown>,
  back?: string | null
) {
  const definitions = fields.definitions
  if (Array.isArray(definitions)) return definitions.join('; ')
  if (typeof definitions === 'string') return definitions
  return back || ''
}

function formFromItem(item: StudyDeckItem | null | undefined): FormState {
  if (!item) return initialForm
  const fields = item.fields || {}
  const source = item.source || {}
  const templatesRaw = fields.enabled_templates
  const templateKeys =
    Array.isArray(templatesRaw) &&
    templatesRaw.every(
      (key): key is StudyTemplateKey => typeof key === 'string'
    )
      ? templatesRaw
      : [item.template_key]
  const sourceTitle =
    typeof source.title === 'string'
      ? source.title
      : typeof fields.source_title === 'string'
        ? fields.source_title
        : ''

  return {
    noteType: item.note_type,
    templateKeys,
    dictEntryId: item.dict_entry_id ? String(item.dict_entry_id) : '',
    simplified: fieldString(fields, 'simplified'),
    traditional: fieldString(fields, 'traditional'),
    pinyin: fieldString(fields, 'pinyin'),
    zhuyin: fieldString(fields, 'zhuyin'),
    definitions: definitionsString(fields, item.back),
    sentence: fieldString(fields, 'text') || item.front,
    translation: fieldString(fields, 'translation') || item.back || '',
    clozeAnswer: fieldString(fields, 'answer'),
    customFront: fieldString(fields, 'front') || item.front,
    customBack: fieldString(fields, 'back') || item.back || '',
    sourceTitle,
    tags: Array.isArray(fields.tags) ? fields.tags.join(', ') : ''
  }
}

function buildInput(form: FormState): StudyNoteInput {
  const tags = form.tags
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean)
  if (form.noteType === 'word') {
    return {
      noteType: 'word',
      dictEntryId: form.dictEntryId ? Number(form.dictEntryId) : null,
      fields: {
        simplified: form.simplified,
        traditional: form.traditional || form.simplified,
        pinyin: form.pinyin,
        zhuyin: form.zhuyin,
        definitions: form.definitions
      },
      source: {},
      tags,
      templateKeys: form.templateKeys
    }
  }
  if (form.noteType === 'sentence') {
    return {
      noteType: 'sentence',
      fields: {
        text: form.sentence,
        translation: form.translation
      },
      source: { title: form.sourceTitle },
      tags,
      templateKeys: form.templateKeys
    }
  }
  if (form.noteType === 'cloze') {
    return {
      noteType: 'cloze',
      fields: {
        text: form.sentence,
        answer: form.clozeAnswer,
        translation: form.translation
      },
      source: { title: form.sourceTitle },
      tags,
      templateKeys: form.templateKeys
    }
  }
  return {
    noteType: 'custom',
    fields: {
      front: form.customFront,
      back: form.customBack
    },
    source: { title: form.sourceTitle },
    tags,
    templateKeys: ['basic']
  }
}

export function StudyNoteDialog({
  deckId,
  item,
  trigger,
  open: controlledOpen,
  onOpenChange,
  onSaved
}: Props) {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [internalOpen, setInternalOpen] = useState(false)
  const open = controlledOpen ?? internalOpen
  const setOpen = onOpenChange ?? setInternalOpen
  const [form, setForm] = useState<FormState>(() => formFromItem(item))

  const { data: fullNote } = useQuery({
    queryKey: ['study-note-editor', item?.note_id, user?.id],
    queryFn: async () => {
      if (!item?.note_id || !user?.id) return null
      return getStudyNoteForEditor(item.note_id, user.id)
    },
    enabled: open && !!item?.note_id && !!user?.id
  })

  useEffect(() => {
    if (!open) return
    const next = formFromItem(item)
    if (fullNote?.cards?.length) {
      next.templateKeys = fullNote.cards.map((card) => card.template_key)
    }
    if (fullNote?.tags?.length) {
      next.tags = fullNote.tags.join(', ')
    }
    const id = window.setTimeout(() => {
      setForm(next)
    }, 0)
    return () => window.clearTimeout(id)
  }, [open, item, fullNote])

  const noteInput = useMemo(() => buildInput(form), [form])
  const errors = useMemo(() => validateStudyNoteInput(noteInput), [noteInput])
  const previews = useMemo(() => generateStudyCards(noteInput), [noteInput])
  const availableTemplates = getAvailableTemplateKeys(form.noteType)

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error('Sign in to save cards')
      return saveStudyNoteToDeck(user.id, deckId, {
        ...noteInput,
        id: item?.note_id
      })
    },
    onSuccess: () => {
      toast.success(item ? 'Card updated' : 'Card added')
      queryClient.invalidateQueries({ queryKey: ['study-deck-items', deckId] })
      queryClient.invalidateQueries({ queryKey: ['all-collections'] })
      onSaved?.()
      setOpen(false)
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : 'Could not save card'
      )
    }
  })

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id || !item?.note_id) return
      await deleteStudyNoteFromDeck(deckId, item.note_id, user.id)
    },
    onSuccess: () => {
      toast.success('Removed from deck')
      queryClient.invalidateQueries({ queryKey: ['study-deck-items', deckId] })
      queryClient.invalidateQueries({ queryKey: ['all-collections'] })
      setOpen(false)
    }
  })

  const setNoteType = (noteType: StudyNoteType) => {
    setForm((current) => ({
      ...current,
      noteType,
      templateKeys: getDefaultTemplateKeys(noteType)
    }))
  }

  const toggleTemplate = (templateKey: StudyTemplateKey, checked: boolean) => {
    setForm((current) => {
      const next = checked
        ? [...current.templateKeys, templateKey]
        : current.templateKeys.filter((key) => key !== templateKey)
      return {
        ...current,
        templateKeys: next.length ? next : [templateKey]
      }
    })
  }
  const setField = (key: FormFieldKey, value: string) => {
    setForm((current) => ({ ...current, [key]: value }))
  }
  const renderField = ({ key, label, area, placeholder }: FormFieldConfig) => {
    const Field = area ? TextAreaField : TextField
    return (
      <Field
        key={key}
        label={label}
        value={form[key]}
        placeholder={placeholder}
        onChange={(value) => setField(key, value)}
      />
    )
  }
  const fieldGroups: Partial<Record<StudyNoteType, FormFieldConfig[]>> = {
    word: [
      { key: 'simplified', label: 'Chinese' },
      { key: 'traditional', label: 'Traditional' },
      { key: 'pinyin', label: 'Pinyin' },
      { key: 'zhuyin', label: 'Zhuyin' },
      { key: 'definitions', label: 'Definitions', area: true, wide: true }
    ],
    sentence: [
      { key: 'sentence', label: 'Chinese sentence', area: true },
      { key: 'translation', label: 'Translation', area: true },
      { key: 'sourceTitle', label: 'Source' }
    ],
    custom: [
      { key: 'customFront', label: 'Front', area: true },
      { key: 'customBack', label: 'Back', area: true },
      { key: 'sourceTitle', label: 'Source' }
    ]
  }
  const activeFields: FormFieldConfig[] =
    form.noteType === 'cloze'
      ? [
          { key: 'sentence' as const, label: 'Chinese sentence', area: true },
          { key: 'clozeAnswer' as const, label: 'Cloze blank' },
          { key: 'translation' as const, label: 'Translation', area: true },
          { key: 'sourceTitle' as const, label: 'Source' }
        ]
      : (fieldGroups[form.noteType] ?? [])

  const content = (
    <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl">
      <DialogHeader>
        <DialogTitle>{item ? 'Edit card' : 'Add card'}</DialogTitle>
        <DialogDescription>
          Choose a note type, fill the fields, then review the generated cards.
        </DialogDescription>
      </DialogHeader>

      <div className="grid gap-5 py-2">
        <div className="grid gap-2">
          <Label>Note type</Label>
          <Select value={form.noteType} onValueChange={setNoteType}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(['word', 'sentence', 'cloze', 'custom'] as StudyNoteType[]).map(
                (type) => (
                  <SelectItem key={type} value={type}>
                    {NOTE_TYPE_LABELS[type]}
                  </SelectItem>
                )
              )}
            </SelectContent>
          </Select>
        </div>

        <div
          className={
            form.noteType === 'word'
              ? 'grid gap-3 sm:grid-cols-2'
              : 'grid gap-3'
          }
        >
          {activeFields.map((field) =>
            field.wide ? (
              <div key={field.key} className="sm:col-span-2">
                {renderField(field)}
              </div>
            ) : (
              renderField(field)
            )
          )}
        </div>

        <TextField
          label="Tags"
          placeholder="travel, grammar, lesson-3"
          value={form.tags}
          onChange={(tags) => setField('tags', tags)}
        />

        <div className="rounded-lg border p-3">
          <div className="mb-3 flex items-center gap-2 text-sm font-medium">
            <IconCards className="h-4 w-4" />
            Card types
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            {availableTemplates.map((templateKey) => (
              <label
                key={templateKey}
                className="hover:bg-muted/50 flex items-center gap-2 rounded-md border px-3 py-2 text-sm"
              >
                <Checkbox
                  checked={form.templateKeys.includes(templateKey)}
                  onCheckedChange={(checked) =>
                    toggleTemplate(templateKey, checked === true)
                  }
                />
                {getTemplateLabel(templateKey)}
              </label>
            ))}
          </div>
        </div>

        <div className="rounded-lg border p-3">
          <div className="mb-3 text-sm font-medium">Preview</div>
          <div className="space-y-2">
            {previews.map((card) => (
              <div
                key={card.template_key}
                className="bg-muted/35 rounded-md p-3"
              >
                <div className="text-muted-foreground text-xs font-medium uppercase">
                  {getTemplateLabel(card.template_key)}
                </div>
                <div className="mt-1 text-sm font-medium">{card.front}</div>
                {card.back && (
                  <div className="text-muted-foreground mt-1 text-sm whitespace-pre-line">
                    {card.back}
                  </div>
                )}
              </div>
            ))}
            {!previews.length && (
              <div className="text-muted-foreground text-sm">
                Fill the required fields to preview generated cards.
              </div>
            )}
          </div>
          {errors.length > 0 && (
            <>
              <Separator className="my-3" />
              <div className="text-destructive text-sm">{errors[0]}</div>
            </>
          )}
        </div>
      </div>

      <DialogFooter className="gap-2 sm:justify-between">
        {item?.note_id ? (
          <Button
            type="button"
            variant="ghost"
            className="text-destructive hover:text-destructive"
            disabled={deleteMutation.isPending || saveMutation.isPending}
            onClick={() => deleteMutation.mutate()}
          >
            <IconTrash className="mr-2 h-4 w-4" />
            Remove
          </Button>
        ) : (
          <span />
        )}
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            disabled={errors.length > 0 || saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            {saveMutation.isPending && (
              <IconLoader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Save
          </Button>
        </div>
      </DialogFooter>
    </DialogContent>
  )

  if (!trigger) {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        {content}
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      {content}
    </Dialog>
  )
}

function TextField({
  label,
  value,
  onChange,
  placeholder
}: {
  label: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
}) {
  return (
    <div className="grid gap-2">
      <Label>{label}</Label>
      <Input
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  )
}

function TextAreaField({
  label,
  value,
  onChange,
  placeholder
}: {
  label: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
}) {
  return (
    <div className="grid gap-2">
      <Label>{label}</Label>
      <Textarea
        value={value}
        placeholder={placeholder}
        rows={3}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  )
}

export function AddStudyNoteButton({ deckId }: { deckId: string }) {
  return (
    <StudyNoteDialog
      deckId={deckId}
      trigger={
        <Button size="sm" className="gap-2">
          <IconPlus className="h-4 w-4" />
          Add card
        </Button>
      }
    />
  )
}
